import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { emptyArray } from 'ui-components/default-props/empty';
import { useModal } from '../../../hooks/use-modal';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { getPackingSlipIds, getStoreConfigurations } from '../../../pages/settings/fulfillment-providers/api/packing-slip-sharing.api';
import { LogService } from '../../../services/log-service';
import { messages } from './messages';
import { PackingSlipSharingErrorModal } from './packing-slip-sharing-error-modal';
import style from './ship-together-destination-modal.module.less';
import { getIsPackingSlipSharing, useFilteredStoreGuids } from '../../../hooks/use-filtered-store-guids';

export const PackingSlipSharingButton = ({ isVendorOr3Pl, fulfillmentPlanIds = emptyArray, onClick }) => {
  const [storeConfigurations, setStoreConfigurations] = useAsyncSafeState(emptyArray);
  const [parsedVouchers, setParsedVouchers] = useAsyncSafeState(emptyArray);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const { open, close, isOpen } = useModal();
  const { storeGuids, auctaneFulfillmentStoreGuids, auctaneFulfillmentOrders } = useFilteredStoreGuids(fulfillmentPlanIds);

  const shouldShowErrorModal = useMemo(() => {
    const uniquePackingSlipIds = parsedVouchers ? [...new Set(parsedVouchers.map((s) => s.packingSlipId))] : emptyArray;
    const isPackingSlipSharing = getIsPackingSlipSharing(storeConfigurations);
    if (isPackingSlipSharing && (uniquePackingSlipIds.length > 1 || storeGuids.length > 1)) {
      return true;
    }
    return false;
  }, [storeConfigurations, storeGuids, parsedVouchers]);

  useOnComponentMount(() => {
    if (isVendorOr3Pl) {
      if (auctaneFulfillmentStoreGuids.length !== 0) {
        const auctaneFulfillmentPlanIds = [...new Set(auctaneFulfillmentOrders.map((s) => s.fulfillmentPlanIds[0]))];
        setIsLoading(true);
        Promise.all([
          getStoreConfigurations({ storeGuids: auctaneFulfillmentStoreGuids }),
          getPackingSlipIds({ fulfillmentIds: auctaneFulfillmentPlanIds }),
        ])
          .then(([r1, r2]) => {
            setStoreConfigurations(r1?.data?.storeConfigurations ?? emptyArray);
            setParsedVouchers(r2?.data?.packingSlipsInfo ?? emptyArray);
          })
          .catch((error) => {
            LogService.logError(error);
          })
          .finally(() => setIsLoading(false));
      }
    }
  });

  const onHandleClick = useCallback(() => {
    if (shouldShowErrorModal) {
      open();
    } else {
      onClick();
    }
  }, [onClick, open, shouldShowErrorModal]);

  return (
    <>
      {isOpen && <PackingSlipSharingErrorModal onClose={close} />}
      <Button className={style['combine-button']} disabled={isLoading} onClick={onHandleClick} theme='primary'>
        <div className={style['combine-button-content']}>
          <div className={style['combine-button-text']}>
            <FormattedMessage {...messages.shipTogetherCombineButton} />
          </div>
          {isLoading && <LoadingIndicator className={style['combine-button-loading-indicator']} theme='white' />}
        </div>
      </Button>
    </>
  );
};

PackingSlipSharingButton.propTypes = {
  isVendorOr3Pl: PropTypes.bool.isRequired,
  fulfillmentPlanIds: PropTypes.array,
  onClick: PropTypes.func.isRequired,
};
